/* ============================================================
   Plan limits — the COUNTED caps in account.tsx PLANS[].caps.

   The boolean caps (workorders, inventory, website…) are screen gates and live
   in app.tsx ROUTE_GATE. These are the numbers a plan allows you to HAVE:

     products  — your own products in the price book (store.customProducts,
                 the same count the price-list import holds to). Built-ins
                 don't count until you make your own copy of one.
     estimates — new estimates (quotes: one record in this app) created this
                 calendar month. Pricing page: "Estimates (new jobs per
                 month)" · "Quotes per month". Sandbox seed rows and inbound
                 homeowner requests (widget / booking form) don't count.
     users     — seats: members plus invites that can still be accepted
                 (mirrors server/src/seats.js seatsUsed). The server holds
                 the real line on invites; this is for the UI.

   Limits only ever stop something NEW being made. Editing, sending, signing
   and deleting what already exists always works, even over a cap (a downgrade
   leaves you over it) — nothing is hidden or removed.

   The plan is resolved the same way as every other gate (window.VFP_PLAN.get
   → settings.plan, which a trial / past-due sub keeps; the sandbox seeds
   Growth). Plain functions + a bottom export: scripts/unit.mjs loads them in
   a vm.
   ============================================================ */

/* the plan's number for a counted cap; Infinity when unlimited or not set */
function planLimit(plan: any, key: string): number {
  const v = plan && plan.caps ? plan.caps[key] : undefined;
  return typeof v === "number" && v >= 0 ? v : Infinity;
}

/* estimates and quotes are one record here — the tighter of the two caps */
function quoteLimit(plan: any): number {
  return Math.min(planLimit(plan, "estimates"), planLimit(plan, "quotes"));
}

function limitStatus(count: any, limit: any) {
  const used = Math.max(0, Math.floor(Number(count) || 0));
  const n = Number(limit);
  const lim = limit == null || isNaN(n) ? Infinity : Math.max(0, n);
  return { used, limit: lim, left: Math.max(0, lim - used), atLimit: used >= lim, over: used > lim };
}

/* epoch ms from a number, a Date, a numeric string or an ISO date; NaN otherwise */
function toMs(v: any): number {
  if (v == null || v === "") return NaN;
  const n = typeof v === "number" ? v : typeof v.getTime === "function" ? v.getTime()
    : /^\d+$/.test(String(v)) ? Number(v) : Date.parse(String(v));
  return isFinite(n) ? n : NaN;
}

/* [start, end) of the calendar month `now` falls in, local time */
function monthBounds(now: any) {
  const d = new Date(toMs(now) || Date.now());
  return { start: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), end: new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime() };
}

/* a homeowner's own request (in-app widget / booking form), not a quote the
   contractor wrote — never counted, never refused */
function inboundQuote(q: any): boolean {
  return !!q && (q.source === "widget" || q.source === "booking");
}

function countsAsEstimate(q: any): boolean {
  return !!q && !q.demoSeed && !inboundQuote(q);
}

function estimatesThisMonth(quotes: any, now: any): number {
  const { start, end } = monthBounds(now);
  return (Array.isArray(quotes) ? quotes : []).filter((q: any) => {
    if (!countsAsEstimate(q)) return false;
    const t = toMs(q.createdAt);
    return t >= start && t < end;
  }).length;
}

/* may this quote be added? Inbound requests always; otherwise room this month */
function canAddQuote(quotes: any, q: any, limit: any, now: any): boolean {
  if (!countsAsEstimate(q)) return true;
  return !limitStatus(estimatesThisMonth(quotes, now), limit).atLimit;
}

function ownProductCount(customProducts: any): number {
  return (Array.isArray(customProducts) ? customProducts : []).filter(Boolean).length;
}

/* may this product be saved? An id already in the catalog (your own, or a
   built-in you're editing) is an EDIT and always saves; a new id needs room */
function canSaveProduct(customProducts: any, builtinIds: any, product: any, limit: any): boolean {
  const id = product && product.id;
  const own = Array.isArray(customProducts) ? customProducts : [];
  if (id != null && own.some((p: any) => p && p.id === id)) return true;
  if (id != null && Array.isArray(builtinIds) && builtinIds.includes(id)) return true;
  return !limitStatus(ownProductCount(own), limit).atLimit;
}

/* seats in use: members + pending invites that haven't expired. `team` is the
   /team/members answer ({ members, invites }). */
function activeUsers(team: any, now: any): number {
  const t = toMs(now) || Date.now();
  const members = (team && Array.isArray(team.members) ? team.members : []).filter(Boolean).length;
  const pending = (team && Array.isArray(team.invites) ? team.invites : []).filter((i: any) =>
    i && (!i.status || i.status === "pending") && (!i.expires_at || toMs(i.expires_at) > t)).length;
  return members + pending;
}

export { planLimit, quoteLimit, limitStatus, toMs, monthBounds, inboundQuote, countsAsEstimate, estimatesThisMonth, canAddQuote, ownProductCount, canSaveProduct, activeUsers };
